import * as React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Avatar,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import HandshakeIcon from "@mui/icons-material/Handshake";
import DescriptionIcon from "@mui/icons-material/Description";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import VerifiedIcon from "@mui/icons-material/Verified";
import TrustIcon from "@mui/icons-material/Security";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import GavelIcon from "@mui/icons-material/Gavel";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Button from "@mui/material/Button";
import MapIcon from "@mui/icons-material/Map";

function AboutUs() {
  const navigate = useNavigate();

  const services = [
    {
      icon: <HomeIcon sx={{ fontSize: 40, color: "#1976d2" }} />,
      title: "Smart Property Matching",
      description: "Helping tenants find quality homes within their budget",
    },
    {
      icon: <AccountBalanceWalletIcon sx={{ fontSize: 40, color: "#1976d2" }} />,
      title: "Complete Financial Tracking",
      description: "Maintaining detailed records of advance payments, monthly rent, and all transactions",
    },
    {
      icon: <HandshakeIcon sx={{ fontSize: 40, color: "#1976d2" }} />,
      title: "Transparent Communication",
      description: "Building trust between owners and tenants through documented agreements",
    },
    {
      icon: <DescriptionIcon sx={{ fontSize: 40, color: "#1976d2" }} />,
      title: "Hassle-Free Management",
      description: "Simplifying the entire rental journey from registration to record-keeping",
    },
  ];

  const coreValues = [
    {
      icon: <VerifiedIcon sx={{ fontSize: 35, color: "#fff" }} />,
      title: "Transparency",
      description: "Clear records, no hidden terms",
      color: "#2196f3",
    },
    {
      icon: <TrustIcon sx={{ fontSize: 35, color: "#fff" }} />,
      title: "Trust",
      description: "Building reliable relationships between owners and tenants",
      color: "#4caf50",
    },
    {
      icon: <AttachMoneyIcon sx={{ fontSize: 35, color: "#fff" }} />,
      title: "Affordability",
      description: "Making quality housing accessible to everyone",
      color: "#ff9800",
    },
    {
      icon: <GavelIcon sx={{ fontSize: 35, color: "#fff" }} />,
      title: "Accountability",
      description: "Every payment documented, every agreement honored",
      color: "#9c27b0",
    },
    {
      icon: <LightbulbIcon sx={{ fontSize: 35, color: "#fff" }} />,
      title: "Innovation",
      description: "Using technology to solve real-world problems",
      color: "#00bcd4",
    },
    {
      icon: <WorkspacePremiumIcon sx={{ fontSize: 35, color: "#fff" }} />,
      title: "Integrity",
      description: "Honest dealings, fair practices",
      color: "#f44336",
    },
  ];

  const team = [
    {
      name: "Javed Samejo",
      role: "Chief Executive Officer (CEO)",
      initial: "JS",
      color: "#1976d2",
    },
    {
      name: "Saeed Afzal",
      role: "Chief Technology Officer (CTO)",
      initial: "SA",
      color: "#388e3c",
    },
  ];

  return (
    <Box sx={{ bgcolor: "#f5f5f5", minHeight: "100vh" }}>
      {/* Header */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
          color: "white",
          py: 8,
          textAlign: "center",
        }}
      >
        <Container maxWidth="lg">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/")}
            sx={{ color: "white", position: "absolute", left: 20, top: 20 }}
          >
            Back to Home
          </Button>
          <Typography variant="h2" fontWeight="bold" gutterBottom>
            About Us
          </Typography>
          <Typography variant="h5" sx={{ opacity: 0.9, maxWidth: 800, mx: "auto" }}>
            Owner Tenant Solution - Transforming Pakistan's Rental Ecosystem
          </Typography>
        </Container>
      </Box>

      {/* Introduction Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Paper elevation={3} sx={{ p: 5, borderRadius: 4 }}>
          <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
            Who We Are
          </Typography>
          <Typography variant="body1" sx={{ fontSize: 18, lineHeight: 1.8, color: "#555" }}>
            <strong>Owner Tenant Solution (OTS)</strong> is Pakistan's innovative property management
            platform designed to bridge the gap between property owners and tenants. We understand
            the challenges faced by both parties in the rental process – from finding affordable
            housing to maintaining transparent financial records.
          </Typography>
          <Typography variant="body1" sx={{ fontSize: 18, lineHeight: 1.8, color: "#555", mt: 2 }}>
            Our platform serves as a complete solution for rental management, ensuring that every
            transaction is documented, every agreement is clear, and every relationship is built on trust.
          </Typography>
        </Paper>
      </Container>

      {/* Services Section */}
      <Box sx={{ bgcolor: "#e3f2fd", py: 6 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" fontWeight="bold" textAlign="center" gutterBottom>
            What We Provide
          </Typography>
          <Grid container spacing={3} sx={{ mt: 2 }}>
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    textAlign: "center",
                    height: "100%",
                    borderRadius: 3,
                    transition: "transform 0.3s",
                    "&:hover": { transform: "translateY(-5px)" },
                  }}
                >
                  {service.icon}
                  <Typography variant="h6" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                    {service.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {service.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Our Story Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Paper elevation={3} sx={{ p: 5, borderRadius: 4, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white" }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Our Story
          </Typography>
          <Typography variant="body1" sx={{ fontSize: 18, lineHeight: 1.8 }}>
            OTS was born in <strong>2022</strong> from a personal experience. Our CEO, <strong>Javed Samejo</strong>,
            spent years living in rental properties across Pakistan and witnessed firsthand the struggles
            tenants face – unclear agreements, disputed payments, and lack of proper documentation.
          </Typography>
          <Typography variant="body1" sx={{ fontSize: 18, lineHeight: 1.8, mt: 2 }}>
            He realized this wasn't just his problem; it was a nationwide issue affecting millions.
            With a vision to transform Pakistan's rental ecosystem, he partnered with <strong>Saeed Afzal (CTO) </strong>
            to build a solution that protects both owners and tenants through technology and transparency.
          </Typography>
        </Paper>
      </Container>

      {/* Leadership Section */}
      <Box sx={{ bgcolor: "#fafafa", py: 6 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" fontWeight="bold" textAlign="center" gutterBottom>
            Our Leadership
          </Typography>
          <Grid container spacing={4} justifyContent="center" sx={{ mt: 2 }}>
            {team.map((member, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 4,
                    textAlign: "center",
                    borderRadius: 4,
                    transition: "transform 0.3s",
                    "&:hover": { transform: "scale(1.02)" },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 100,
                      height: 100,
                      bgcolor: member.color,
                      fontSize: 36,
                      fontWeight: "bold",
                      mx: "auto",
                      mb: 2,
                    }}
                  >
                    {member.initial}
                  </Avatar>
                  <Typography variant="h5" fontWeight="bold">
                    {member.name}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                    {member.role}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Core Values Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography variant="h4" fontWeight="bold" textAlign="center" gutterBottom>
          Our Core Values
        </Typography>
        <Grid container spacing={3} sx={{ mt: 2 }}>
          {coreValues.map((value, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  transition: "transform 0.3s",
                  "&:hover": { transform: "translateX(10px)" },
                }}
              >
                <Avatar sx={{ bgcolor: value.color, width: 56, height: 56 }}>
                  {value.icon}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {value.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {value.description}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Contact Section */}
      <Box sx={{ bgcolor: "#1a1a2e", color: "white", py: 6 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" fontWeight="bold" textAlign="center" gutterBottom>
            Contact Us
          </Typography>
          <Typography variant="body1" textAlign="center" sx={{ opacity: 0.8, mb: 4 }}>
            Have questions? We'd love to hear from you.
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} sm={6} md={4}>
              <Paper
                elevation={3}
                sx={{
                  p: 4,
                  textAlign: "center",
                  borderRadius: 3,
                  bgcolor: "rgba(255,255,255,0.1)",
                  color: "white",
                }}
              >
                <PhoneIcon sx={{ fontSize: 50, color: "#4caf50", mb: 2 }} />
                <Typography variant="h6" fontWeight="bold">
                  Phone
                </Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  +92-349-2565577
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Paper
                elevation={3}
                sx={{
                  p: 4,
                  textAlign: "center",
                  borderRadius: 3,
                  bgcolor: "rgba(255,255,255,0.1)",
                  color: "white",
                }}
              >
                <MapIcon sx={{ fontSize: 50, color: "#2196f3", mb: 2 }} />
                <Typography variant="h6" fontWeight="bold">
                  Email
                </Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  ownertenantsolution@gmail.com
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Paper
                elevation={3}
                sx={{
                  p: 4,
                  textAlign: "center",
                  borderRadius: 3,
                  bgcolor: "rgba(255,255,255,0.1)",
                  color: "white",
                }}
              >
                <EmailIcon sx={{ fontSize: 50, color: "#2196f3", mb: 2 }} />
                <Typography variant="h6" fontWeight="bold">
                  Email
                </Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  ownertenantsolution@gmail.com
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: "#0d0d1a", color: "white", py: 3, textAlign: "center" }}>
        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          © 2022 - {new Date().getFullYear()} Owner Tenant Solution (OTS). All rights reserved.
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.5, mt: 1 }}>
          Designed & Developed by OTS Team
        </Typography>
      </Box>
    </Box>
  );
}

export default AboutUs;
